import React, { useMemo } from 'react'
import { PrototypeProvider } from '@sketch-hq/sketch-prototype-player'
import { useVersioning } from 'modules/versioning'
import {
  PrototypeViewHeaderEmpty,
  PrototypeViewHeader,
} from './components/PrototypeViewHeader'
import { PrototypeLoadingErrorHandler } from './components/PrototypeLoadingErrorHandler'
import * as Styles from './PrototypeView.styles'
import { getRenderStatus, LoadingPage } from '@sketch/modules-common'
import { useGetPrototypeFileForDocumentQuery } from '@sketch/gql-types'
import {
  usePrototypeStatusSubscription,
  usePrototypeManifest,
  usePrototypeImagesPreload,
  PrototypeViewContextProvider,
  usePrototypeConfig,
  useInitialPrototypeArtboardId,
  usePrototypeLoadDurationMetric,
  usePrototypeImagePreloadDurationMetric,
} from './hooks'
import { ErrorMessage } from '@sketch/components'
import { ReactComponent as DocumentProcessing } from '@sketch/icons/document-processing-64'
import { PrototypePlayer } from './components/PrototypePlayer'
import config from '@sketch/env-config'

/**
 * Small wrapper necessary so that can PrototypeView access the full screen
 * context needed inside usePrototypeConfig.
 */
export function PrototypeViewOld() {
  return (
    <PrototypeViewContextProvider>
      <PrototypeViewInner />
    </PrototypeViewContextProvider>
  )
}

/**
 * Before rendering the player, this view has to coordinate a fairly complex
 * serial sequence of actions:
 *
 * 1. Wait for the versioning context to load
 * 2. Query GraphQL for the `Document.prototypeFile` data
 * 3. Use the `prototypeFile` subscription to wait for the prototype to be
 *    rendered on demand, if necessary
 * 4. Load the `manifest.json` file
 * 5. Preload the prototype's images, displaying load progress
 * 6. Finally show the player
 */
function PrototypeViewInner() {
  const versioning = useVersioning()
  const initialArtboardId = useInitialPrototypeArtboardId()
  const documentRenderStatus = !versioning.loading
    ? getRenderStatus(versioning.currentVersion.document?.renderStatus ?? null)
    : null

  const fileQuery = useGetPrototypeFileForDocumentQuery({
    variables: {
      identifier: versioning.share.identifier,
      versionShortId: versioning.versionShortId,
    },
    skip: documentRenderStatus !== 'ready',
  })

  const prototypeFile = fileQuery?.data?.share?.version?.document?.prototypeFile
  const documentIdentifier =
    fileQuery?.data?.share?.version?.document?.identifier

  usePrototypeStatusSubscription({
    documentRenderStatus,
    documentIdentifier,
    prototypeFile,
  })

  const manifestResult = usePrototypeManifest({
    downloadUrl: prototypeFile?.downloadUrl,
  })

  // Function that transforms an image asset `path` from the manifest.json into
  // the full url for the image in the Cloud infrastructure. We define this on this
  // level since it's used by both the player and the images preloader
  const resolveImageSrc = useMemo(() => {
    if (!prototypeFile) return null
    const { taskId, imageToken } = prototypeFile
    return (path: string) => {
      return `${config.prototype_player_image_domain}/prototype/t/${taskId}/i/${path}?token=${imageToken}`
    }
  }, [prototypeFile])

  const prototypeConfig = usePrototypeConfig({ resolveImageSrc })

  const imagesPreload = usePrototypeImagesPreload({
    manifest:
      (manifestResult.status === 'success' && manifestResult.manifest) || null,
    resolveImageSrc,
    initialArtboardId,
  })

  usePrototypeImagePreloadDurationMetric({
    imagePreloadStatus: imagesPreload.imagePreloadStatus,
    imageCount: imagesPreload.imagesCount,
  })

  usePrototypeLoadDurationMetric({
    loaded: imagesPreload.imagePreloadStatus === 'completed',
    prototypeFile,
  })
  if (versioning.loading || fileQuery.loading) {
    return <LoadingState />
  }

  if (fileQuery.error || !prototypeFile) {
    return <PrototypeLoadingErrorHandler code="query_error" />
  }

  if (documentRenderStatus !== 'ready' || prototypeFile.status === 'PENDING') {
    return <ProcessingState />
  }

  if (manifestResult.status === 'loading') return <LoadingState />

  if (manifestResult.status === 'error') {
    return <PrototypeLoadingErrorHandler code={manifestResult.code} />
  }

  if (imagesPreload.imagePreloadStatus === 'error') {
    return <PrototypeLoadingErrorHandler code="image_preload_error" />
  }

  if (imagesPreload.imagePreloadStatus !== 'completed')
    return <LoadingState progress={imagesPreload.imagePreloadProgress} />

  return (
    <PrototypeProvider
      config={prototypeConfig}
      manifest={manifestResult.manifest}
    >
      <Styles.StyledPageLayout darkBackground header={<PrototypeViewHeader />}>
        <PrototypePlayer />
      </Styles.StyledPageLayout>
    </PrototypeProvider>
  )
}

function LoadingState({ progress }: { progress?: number }) {
  return (
    <Styles.StyledPageLayout header={<PrototypeViewHeaderEmpty />}>
      <LoadingPage.Prototype progress={progress} />
    </Styles.StyledPageLayout>
  )
}

function ProcessingState() {
  return (
    <Styles.StyledPageLayout header={<PrototypeViewHeaderEmpty />}>
      <ErrorMessage
        dark
        title="Rendering Prototype…"
        description="The Prototype is still rendering. When it’s ready, it will appear here."
        icon={<DocumentProcessing />}
      />
    </Styles.StyledPageLayout>
  )
}
